import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import {
  useBrowserStore,
  usePaxStore,
} from '@apps/main/new-desktop/core/stores';
import { i18n } from '@apps/main/modules/localization/localizationSetup';
import type { EditTripStep } from '@apps/main/new-desktop/modules/editTripModal';

import type { AppTabType } from '@libs/ui';

import type {
  ConfigurationEditTripDetails,
  ConfigurationPanelPaxDetails,
} from '../models/configurationPanel.model';
import { TabKey } from '../models/configurationPanel.model';

import { useEditTripStore } from './editTrip';

export const useConfigurationPanelStore = defineStore(
  'ConfigurationPanelStore',
  () => {
    const paxStore = usePaxStore();
    const editTripStore = useEditTripStore();
    const browserStore = useBrowserStore();
    const { t } = i18n.global;

    // State
    const tabSelected = ref<TabKey>(TabKey.None);
    const editing = ref<boolean>(false);
    const currentStep = ref<EditTripStep>(editTripStore.initialStep);

    const destinationsTab = {
      title: t('planner.configuration_panel.tabs.destinations'),
      icon: 'itinerary' as const,
      key: TabKey.Itinerary,
    };
    const travelersTab = {
      title: t('planner.configuration_panel.tabs.travelers'),
      icon: 'people' as const,
      key: TabKey.Pax,
    };
    const dateTab = {
      title: t('planner.configuration_panel.tabs.date'),
      icon: 'calendarOutlined' as const,
      key: TabKey.Calendar,
    };

    // Actions
    function changeEditStep(value: EditTripStep): void {
      currentStep.value = value;
    }

    function handleTabSwitch(tabKey: TabKey): void {
      tabSelected.value = tabKey;
    }

    function updateEditing(isEditing: boolean): void {
      editing.value = isEditing;
    }

    // Getters
    const getPaxDetails = computed<ConfigurationPanelPaxDetails | undefined>(
      () => ({
        title: paxStore.getPaxesButtonSimpleText,
        initialPaxes: paxStore.initialPaxes,
        shouldShowRoomSelectionWarning: paxStore.shouldShowRoomSelectionWarning,
        shouldShowTicketsWarning: paxStore.shouldShowTicketsWarning,
      })
    );

    const getTabSelected = computed<TabKey>(() => tabSelected.value);

    function getConfigurationPanelTabs(
      showFlightTab: boolean,
      shouldReorderTabs: boolean
    ): AppTabType[] {
      const flyFromTab = {
        title: t('planner.configuration_panel.tabs.fly_from'),
        icon: 'airplaneTakeoff' as const,
        key: TabKey.Airplane,
      };

      const tabs: AppTabType[] = [
        shouldReorderTabs ? destinationsTab : travelersTab,
        dateTab,
        shouldReorderTabs ? travelersTab : destinationsTab,
      ];

      if (showFlightTab) {
        tabs.push(flyFromTab);
      }

      return tabs;
    }

    const editTripDetails = computed<ConfigurationEditTripDetails | undefined>(
      () => ({
        segments: editTripStore.modalData,
        isDesktop: browserStore.isSmallerThanSmallMin,
        hasBookableTransport: editTripStore.hasBookableTransport,
        initialStep: currentStep.value,
      })
    );

    const isEditing = computed<boolean>(() => editing.value);

    return {
      // Actions
      handleTabSwitch,
      updateEditing,
      changeEditStep,

      // Getters
      getPaxDetails,
      getTabSelected,
      getConfigurationPanelTabs,
      editTripDetails,
      isEditing,
    };
  }
);
